import React, { useCallback, useMemo, useEffect } from 'react';
import DT from 'duration-time-conversion';
import { useDispatch, useSelector } from 'react-redux';
import { searchValueInText } from '../../utils';
import { getKeywordNameFromPath } from '../../../../store/helpers/keywords';
import { changeTree } from '../../../../store/actions/main';
import { setSearchRoute } from '../../../../helpers/history';
import useCurrentItemIndex from '../../utils/useCurrentItemIndex';
import TranscriptItem from '../TranscriptItem';
import PlaceholderEmpty from '../PlaceholderEmpty';
import OPTIONS from '../ViewsSelector/options';

const option = OPTIONS.find((o) => o.value === 'keywords') || {};
const ENTITY = option.text || 'Keywords';
const ICON = option.icon;

const Keywords = ({
  keywords = [], searchValue, currentTime, setVideoPlayerCurrentTime, revisionId, assetKeywords = [], assetId,
}) => {
  const dispathch = useDispatch();
  const openedTree = useSelector((state) => state.main.openedTree);
  const rootCollectionId = useSelector((state) => state.collections.collections?.my?._id);

  const handleClickKeyword = useCallback((id) => (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (openedTree !== 'keywords') dispathch(changeTree('keywords'));

    setSearchRoute({ collectionIds: rootCollectionId, keywords: id });
  }, [dispathch, openedTree, rootCollectionId]);

  const items = useMemo(() => {
    const preparedKeywords = keywords.map((keyword) => {
      const assetKeyword = assetKeywords.find(
        (ak) => getKeywordNameFromPath(ak.path).toLowerCase() === keyword.text.toLowerCase(),
      );
      if (assetKeyword) {
        return {
          ...keyword,
          _id: assetKeyword._id,
        };
      }
      return null;
    }).filter(Boolean);

    const sortedSegments = preparedKeywords
      .reduce((acc, curr) => {
        const { text, _id } = curr;
        curr.segments.forEach((segment) => {
          acc.push({ ...segment, text, _id });
        });
        return acc;
      }, [])
      .sort((a, b) => DT.t2d(a.startTime) - DT.t2d(b.startTime))
      .map((item, index) => ({ ...item, markerOrder: index + 1 }));

    if (searchValue) {
      return sortedSegments.filter(({ text }) => text.toLowerCase().includes(searchValue.toLowerCase()));
    }

    return sortedSegments;
  }, [assetKeywords, keywords, searchValue]);

  useEffect(() => {
    window.dispatchEvent(new CustomEvent('preview:videoAiView:keywordsSearch', {
      detail: {
        assetId,
        items,
      },
    }));
  }, [assetId, items]);

  const [activeItemIndex, setTime] = useCurrentItemIndex(items, currentTime, setVideoPlayerCurrentTime);

  if (!items.length) return <PlaceholderEmpty entity={ENTITY} icon={ICON} isSearch={!!searchValue} />;

  return items.map(({
    startTime, endTime, text, _id,
  }, i) => (
    <TranscriptItem
      key={startTime + endTime + _id}
      setVideoPlayerCurrentTime={setTime}
      activeRevisionID={revisionId}
      startTime={startTime}
      onClickText={handleClickKeyword(_id)}
      tooltipText="Search all assets for this keyword"
      endTime={endTime}
      text={searchValueInText(text, searchValue)}
      isActive={i === activeItemIndex}
      assetId={assetId}
      clickEvent="VideoIntelligenceKeywordsEntityClicked"
    />
  ));
};

export default Keywords;
